<script setup>
import NewsPageAside from "@/components/NewsPage/NewsPageAside.vue";
import NewsPageNewsFeed from "@/components/NewsPage/NewsPageNewsFeed.vue";
import NewsPageRoof from "@/components/NewsPage/NewsPageRoof/NewsPageRoof.vue";
import NewsPageBodyPost from "@/components/NewsPage/NewsPageBody/NewsPageBodyPost.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import NewsPageBodyEmptyNews from "@/components/NewsPage/NewsPageBody/NewsPageBodyEmptyNews.vue";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import { computed, ref, onMounted, onUnmounted } from "vue";

const rubricsStore = useRubricsStore();
const newsFeedStore = useNewsFeedStore();

// Ref для контейнера новостей
const containerNews = ref(null);
const containerWidth = ref(0);

// Отображение тулбтара для работы с выделенными карточками.
const isToolbarActive = computed(() => rubricsStore.isCurrentRubricFixed());

// Список выделенных карточек.
const chosenCardList = ref([]);

// Выбор всех карточек на странице
const toggleAllCards = () => {
  if (isAllPageCardChosen.value) {
    const set1 = new Set(pageCardIdList.value);
    chosenCardList.value = chosenCardList.value.filter(item => !set1.has(item));
  } else {
    chosenCardList.value = pageCardIdList.value;
  }
};

// ID Всех карточек на странице
const pageCardIdList = computed(() => newsFeedStore.items.map((item) => item.id));
// Выбраны ли все карточки на странице.
const isAllPageCardChosen = computed(() => {
  const pageIdList = pageCardIdList.value;
  const selectedIdList = chosenCardList.value;

  const selectedSet = new Set(selectedIdList);
  return pageIdList.every(id => selectedSet.has(id));
});

// Обновление ширины контейнера
const updateContainerWidth = () => {
  if (containerNews.value) {
    containerWidth.value = containerNews.value.offsetWidth;
  }
};

onMounted(() => {
  updateContainerWidth();
  window.addEventListener("resize", updateContainerWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateContainerWidth);
});

</script>
<template>
  <div class="news-page-wrapper">
    <NewsPageRoof
      :is-toolbar-active="isToolbarActive"
      class="container-roof"
    />
    <div
      id="containerNews"
      ref="containerNews"
      class="container-news"
    >
      <NewsPageAside
        v-if="rubricsStore.allRubrics.length"
        class="container-news__aside"
      />
      <div
        v-if="newsFeedStore.items.length === 0 && newsFeedStore.loading"
        class="container-news__loader"
      >
        <LocalLoader />
      </div>
      <template v-else-if="newsFeedStore.items.length > 0">
        <NewsPageNewsFeed 
          v-model:chosen-card-list="chosenCardList"
          :is-all-page-card-chosen="isAllPageCardChosen"
          :is-toolbar-active="isToolbarActive"
          :parent-container-width="containerWidth"
          class="container-news__newsfeed"
          :class="{ nopointer: newsFeedStore.pointer }"
          @toggle-all-cards="toggleAllCards"
        />
        <NewsPageBodyPost
          v-if="newsFeedStore.currentNews && newsFeedStore.items?.length && !newsFeedStore.loading"
          :key="newsFeedStore.currentNews.id"
          class="container-news__post"
          :class="{ nopointer: newsFeedStore.pointer }"
        />
        <div
          v-else-if="newsFeedStore.items.length > 0 && newsFeedStore.loading"
          class="container-news__post--loader"
        >
          <LocalLoader />
        </div>
      </template>
      <NewsPageBodyEmptyNews v-else-if="newsFeedStore.emptyNews || (rubricsStore.chosenRubrics.length === 0)" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.news-page-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: calc(100vh - 80px);

  .container-news {
    display: grid;
    grid-template-columns: 231px minmax(450px, auto) minmax(471px, 1fr);
    overflow: hidden;
    gap: 14px;
    height: 100%;
    position: relative;

    &__loader {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }

    &__header {
      grid-column: 1/4;
    }

    &__aside {
      grid-column: 1/2;
    }

    &__newsfeed {
      grid-column: 2/3;

      &.nopointer {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    }

    &__post {
      grid-column: 3/4;

      &--loader {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
      }

      &.nopointer {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    }
  }
}
</style>
